import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getSubmission,
  getSubmissionFileSignedUrl,
  listSubmissionFiles,
  listSubmissionPages,
  promoteToCorpus,
  revertToProduction,
  subscribeToSubmission,
} from '../lib/db.ts';
import type { Submission, SubmissionFile, SubmissionPage } from '../lib/types.ts';
import { StatusBadge } from '../components/StatusBadge.tsx';
import { buildGroundTruth, unreviewedFlaggedPages } from '../lib/groundTruth.ts';
import { gradeJob } from '../lib/grading.ts';

interface DetailState {
  submission: Submission | null;
  files: readonly SubmissionFile[];
  pages: readonly SubmissionPage[];
  error: string | null;
}

const EMPTY: DetailState = { submission: null, files: [], pages: [], error: null };

export function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<DetailState>(EMPTY);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let active = true;

    async function load(): Promise<void> {
      try {
        const [submission, files, pages] = await Promise.all([
          getSubmission(id!),
          listSubmissionFiles(id!),
          listSubmissionPages(id!),
        ]);
        if (active) setState({ submission, files, pages, error: null });
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Load failed';
        if (active) setState((prev) => ({ ...prev, error: message }));
      } finally {
        if (active) setLoading(false);
      }
    }

    void load();

    const channel = subscribeToSubmission({
      submissionId: id,
      onSubmissionChange: (next) => {
        if (active) setState((prev) => ({ ...prev, submission: next }));
      },
      onPagesChange: () => void load(),
      onFilesChange: () => void load(),
    });

    return () => {
      active = false;
      void channel.unsubscribe();
    };
  }, [id]);

  if (loading) {
    return <div style={{ color: '#525870', fontSize: 13 }}>Loading…</div>;
  }
  if (state.error) {
    return (
      <div style={{ color: '#FF6B7A', fontSize: 13, padding: 16, border: '1px solid rgba(255,107,122,0.3)', borderRadius: 8 }}>
        {state.error}
      </div>
    );
  }
  if (!state.submission) {
    return (
      <div style={{ color: '#B8BDD6', fontSize: 13 }}>
        Submission not found.{' '}
        <Link to="/app/jobs" style={{ color: '#00E87A' }}>
          Back to jobs
        </Link>
      </div>
    );
  }

  const { submission, files, pages } = state;
  const flagged = pages.filter((p) => p.status === 'low_confidence' || p.status === 'unclassified' || p.status === 'needs_ocr');

  return (
    <div style={{ maxWidth: 960 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        <Link to="/app/jobs" style={{ color: '#525870', fontSize: 12, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.1em' }}>
          ← JOBS
        </Link>
        <StatusBadge status={submission.status} />
      </div>

      <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 28, fontWeight: 800, marginBottom: 4 }}>
        {submission.job_name}
      </h1>
      <div style={{ color: '#525870', fontSize: 12, fontFamily: "'JetBrains Mono', monospace", marginBottom: 24 }}>
        {submission.source_pdf} · {submission.kind} · created {new Date(submission.created_at).toLocaleString()}
      </div>

      {submission.consumer_error && (
        <div
          style={{
            padding: 14,
            border: '1px solid rgba(255,107,122,0.3)',
            background: 'rgba(255,107,122,0.06)',
            borderRadius: 8,
            color: '#FF6B7A',
            fontSize: 13,
            marginBottom: 24,
          }}
        >
          <strong>Consumer error:</strong> {submission.consumer_error}
        </div>
      )}

      <Section
        title="Pages"
        subtitle={`${pages.length} total · ${flagged.length} flagged for review`}
        action={
          flagged.length > 0 ? (
            <Link
              to={`/app/jobs/${submission.id}/review`}
              style={{
                background: 'transparent',
                border: '1px solid rgba(0,232,122,0.4)',
                color: '#00E87A',
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10,
                letterSpacing: '0.1em',
                padding: '6px 12px',
                borderRadius: 5,
                textTransform: 'uppercase' as const,
                textDecoration: 'none',
              }}
            >
              Review flagged →
            </Link>
          ) : null
        }
      >
        {pages.length === 0 ? (
          <Hint text="The parser hasn't written page rows yet. They'll appear here once parsing starts." />
        ) : (
          <PageGrid pages={pages} />
        )}
      </Section>

      <Section title="Files" subtitle={`${files.length} discipline-split outputs`}>
        {files.length === 0 ? (
          <Hint text="Discipline-split PDFs will land here once the parser finishes." />
        ) : (
          <FileList files={files} />
        )}
      </Section>

      <CorpusSection
        submission={submission}
        pages={pages}
        onChange={(next) => setState((prev) => ({ ...prev, submission: next }))}
      />

      {submission.status === 'processed' && (
        <Section title="Takeoff" subtitle="rebar engine · onscreen civil · live weight summary">
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Link
              to={`/app/jobs/${submission.id}/takeoff/rebar`}
              style={{
                background: 'rgba(0,232,122,0.08)',
                border: '1px solid rgba(0,232,122,0.4)',
                color: '#00E87A',
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10,
                letterSpacing: '0.1em',
                padding: '8px 14px',
                borderRadius: 5,
                textTransform: 'uppercase' as const,
                textDecoration: 'none',
              }}
            >
              Open structural takeoff →
            </Link>
            <Link
              to={`/app/jobs/${submission.id}/takeoff/civil`}
              style={{
                background: 'rgba(196,155,255,0.08)',
                border: '1px solid rgba(196,155,255,0.4)',
                color: '#C49BFF',
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10,
                letterSpacing: '0.1em',
                padding: '8px 14px',
                borderRadius: 5,
                textTransform: 'uppercase' as const,
                textDecoration: 'none',
              }}
            >
              Open civil takeoff →
            </Link>
            <Link
              to={`/app/jobs/${submission.id}/output`}
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.18)',
                color: '#F0F2FF',
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10,
                letterSpacing: '0.1em',
                padding: '8px 14px',
                borderRadius: 5,
                textTransform: 'uppercase' as const,
                textDecoration: 'none',
              }}
            >
              Render output →
            </Link>
          </div>
        </Section>
      )}
    </div>
  );
}

interface SectionProps {
  title: string;
  subtitle: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}

function Section({ title, subtitle, action, children }: SectionProps) {
  return (
    <section style={{ marginBottom: 30 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
          <h2 style={{ fontFamily: "'Syne', sans-serif", fontSize: 18, fontWeight: 700 }}>{title}</h2>
          <span style={{ color: '#525870', fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>{subtitle}</span>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function Hint({ text }: { text: string }) {
  return (
    <div
      style={{
        padding: '14px 16px',
        background: '#0D0F1C',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 8,
        color: '#B8BDD6',
        fontSize: 13,
      }}
    >
      {text}
    </div>
  );
}

function PageGrid({ pages }: { pages: readonly SubmissionPage[] }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
      {pages.map((p) => {
        const flagged = p.status !== 'classified';
        return (
          <div
            key={p.id}
            title={p.category ?? 'unclassified'}
            style={{
              padding: '8px 10px',
              borderRadius: 6,
              background: flagged ? 'rgba(255,107,122,0.06)' : '#0D0F1C',
              border: `1px solid ${flagged ? 'rgba(255,107,122,0.25)' : 'rgba(255,255,255,0.06)'}`,
              fontSize: 12,
            }}
          >
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#525870' }}>
              p{p.page_index} {p.page_label ? `· ${p.page_label}` : ''}
            </div>
            <div style={{ color: flagged ? '#FF6B7A' : '#F0F2FF', marginTop: 2, fontWeight: 500 }}>
              {p.category ?? '—'}
            </div>
            {p.confidence !== null && (
              <div style={{ color: '#525870', fontSize: 10, fontFamily: "'JetBrains Mono', monospace" }}>
                {Math.round(p.confidence * 100)}%
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface CorpusSectionProps {
  submission: Submission;
  pages: readonly SubmissionPage[];
  onChange: (next: Submission) => void;
}

function CorpusSection({ submission, pages, onChange }: CorpusSectionProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isCorpus = submission.kind === 'corpus';
  const blockingPages = unreviewedFlaggedPages(pages);
  const canPromote = !isCorpus && blockingPages.length === 0 && pages.length > 0;
  const promotedAt = isCorpus ? submission.ground_truth?.promoted_at ?? null : null;
  const groundTruthPageCount = isCorpus ? submission.ground_truth?.pages.length ?? 0 : 0;

  async function handlePromote(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const groundTruth = buildGroundTruth({ pages, promotedFromStatus: submission.status });
      const next = await promoteToCorpus(submission.id, groundTruth);
      onChange(next);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Promote failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleRevert(): Promise<void> {
    if (!confirm('Revert this corpus job to production? The ground-truth snapshot will be cleared.')) return;
    setBusy(true);
    setError(null);
    try {
      const next = await revertToProduction(submission.id);
      onChange(next);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Revert failed');
    } finally {
      setBusy(false);
    }
  }

  const subtitle = isCorpus
    ? `corpus · ${groundTruthPageCount} pages snapshotted${promotedAt ? ` · promoted ${new Date(promotedAt).toLocaleString()}` : ''}`
    : blockingPages.length > 0
    ? `${blockingPages.length} flagged page${blockingPages.length === 1 ? '' : 's'} still unreviewed`
    : 'production · ready to promote';

  const grade = isCorpus ? gradeJob(submission, pages) : null;

  return (
    <Section title="Corpus" subtitle={subtitle}>
      {error && (
        <div style={{ color: '#FF6B7A', fontSize: 12, marginBottom: 10 }}>{error}</div>
      )}
      {grade && (
        <Link
          to={`/app/jobs/${submission.id}/output`}
          style={{
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 8,
            padding: '6px 12px',
            border: `1px solid ${gradeChipColor(grade.scorePct)}`,
            background: `color-mix(in srgb, ${gradeChipColor(grade.scorePct)} 10%, transparent)`,
            borderRadius: 6,
            color: gradeChipColor(grade.scorePct),
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '0.05em',
            textDecoration: 'none',
            marginBottom: 10,
          }}
        >
          GRADE <strong style={{ fontSize: 14 }}>{grade.scorePct}%</strong>
          <span style={{ color: '#525870' }}>· {grade.totalGraded} graded</span>
        </Link>
      )}
      {isCorpus ? (
        <button
          type="button"
          onClick={() => void handleRevert()}
          disabled={busy}
          style={corpusBtnStyle('#FF6B7A', busy)}
        >
          {busy ? 'Reverting…' : 'Revert to production'}
        </button>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            type="button"
            onClick={() => void handlePromote()}
            disabled={!canPromote || busy}
            style={corpusBtnStyle('#00E87A', !canPromote || busy)}
          >
            {busy ? 'Promoting…' : 'Promote to corpus'}
          </button>
          {!canPromote && blockingPages.length > 0 && (
            <span style={{ color: '#525870', fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
              Review pages: {blockingPages.slice(0, 8).map((i) => `p${i + 1}`).join(', ')}
              {blockingPages.length > 8 ? ` +${blockingPages.length - 8} more` : ''}
            </span>
          )}
        </div>
      )}
    </Section>
  );
}

function gradeChipColor(pct: number): string {
  if (pct >= 90) return '#00E87A';
  if (pct >= 70) return '#C49BFF';
  if (pct >= 50) return '#FFD56B';
  return '#FF6B7A';
}

function corpusBtnStyle(color: string, disabled: boolean): React.CSSProperties {
  return {
    background: disabled ? 'rgba(255,255,255,0.04)' : `color-mix(in srgb, ${color} 12%, transparent)`,
    border: `1px solid ${disabled ? 'rgba(255,255,255,0.1)' : color}`,
    color: disabled ? '#525870' : color,
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 11,
    letterSpacing: '0.1em',
    padding: '8px 14px',
    borderRadius: 6,
    textTransform: 'uppercase',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    alignSelf: 'flex-start',
  };
}

function FileList({ files }: { files: readonly SubmissionFile[] }) {
  return (
    <ul style={{ listStyle: 'none' }}>
      {files.map((f) => (
        <li
          key={f.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 14px',
            background: '#0D0F1C',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 8,
            marginBottom: 6,
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <span style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {f.filename}
            </span>
            <span style={{ color: '#525870', fontSize: 10, fontFamily: "'JetBrains Mono', monospace" }}>
              {f.discipline} · {f.size_bytes ? `${(f.size_bytes / 1024 / 1024).toFixed(2)} MB` : 'unknown size'}
            </span>
          </div>
          <button
            type="button"
            onClick={async () => {
              const url = await getSubmissionFileSignedUrl(f.storage_path);
              window.open(url, '_blank', 'noopener');
            }}
            style={{
              background: 'transparent',
              border: '1px solid rgba(0,232,122,0.4)',
              color: '#00E87A',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10,
              letterSpacing: '0.1em',
              padding: '6px 12px',
              borderRadius: 5,
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            Download
          </button>
        </li>
      ))}
    </ul>
  );
}
